import { useEffect } from 'react'

import { summarizeObjects } from 'utils/object'
import { useApiGetListPenilaianTahunanDinas } from 'api/kinerja/kepala-sekolah/dinas/penilaian-tahunan'
import { useApiGetListPenilaianTahunanDinasTabGuru } from 'api/kinerja/kepala-sekolah/guru/dinas/penilaian-tahunan'

import type {
  TPenilaianTahunanStatePengiriman,
  TPenilaianTahunanStatePengirimanComplete,
  TResponseListPenilaianTahunan,
} from 'types/kinerja-dinas-penilaian'
import type { TabFilterRoles } from 'types/kinerja-ks'

type TProps = {
  year: number
  isAccessible: boolean
  activeTabName?: TabFilterRoles
  statusPengiriman?: TPenilaianTahunanStatePengirimanComplete[]
  onBothFetched?: () => void
}

type TResponse = {
  // Kepala Sekolah
  isListLoadingKs: boolean
  isListErrorKs: boolean
  isListSuccessKs: boolean
  itemsKs: TResponseListPenilaianTahunan['data']
  itemsCountKs: number
  metasKs: TResponseListPenilaianTahunan['meta']
  refetchListKs: () => void

  // Guru
  isListLoadingGuru: boolean
  isListErrorGuru: boolean
  isListSuccessGuru: boolean
  itemsGuru: TResponseListPenilaianTahunan['data']
  itemsCountGuru: number
  metasGuru: TResponseListPenilaianTahunan['meta']
  refetchListGuru: () => void

  // Combined
  isListLoading: boolean
  isListError: boolean
  isListSuccess: boolean
  isListBothFetched: boolean
  metas: TResponseListPenilaianTahunan['meta']
  totalItems: number
  hasItems: boolean

  // Relative to active tab
  items: TResponseListPenilaianTahunan['data']
  relativeItems: TResponseListPenilaianTahunan['data']
  isRelativeLoading: boolean
  isRelativeError: boolean
  isRelativeSuccess: boolean
}

const usePenetapanTahunanDinas = (props: TProps): TResponse => {
  const { year, isAccessible, activeTabName, statusPengiriman, onBothFetched } =
    props || {}

  // Calculations for Kepala Sekolah
  const {
    isLoading: isListLoadingKs,
    isError: isListErrorKs,
    isSuccess: isListSuccessKs,
    isFetched: isListFetchedKs,
    data: { data: itemsKs, meta: metasKs } = {},
    refetch: refetchListKs,
  } = useApiGetListPenilaianTahunanDinas(
    {
      year,
      statusPengiriman: statusPengiriman?.includes('ALL')
        ? undefined
        : (statusPengiriman as TPenilaianTahunanStatePengiriman[]),
    },
    { enabled: isAccessible }
  )

  // Calculations for Guru
  const {
    isLoading: isListLoadingGuru,
    isError: isListErrorGuru,
    isSuccess: isListSuccessGuru,
    isFetched: isListFetchedGuru,
    data: { data: itemsGuru, meta: metasGuru } = {},
    refetch: refetchListGuru,
  } = useApiGetListPenilaianTahunanDinasTabGuru(
    {
      year,
      statusPengiriman: statusPengiriman?.includes('ALL')
        ? undefined
        : (statusPengiriman as TPenilaianTahunanStatePengiriman[]),
    },
    { enabled: isAccessible }
  )

  const isListLoading = isListLoadingKs || isListLoadingGuru
  const isListError = isListErrorKs || isListErrorGuru
  const isListSuccess = isListSuccessKs || isListSuccessGuru
  const isListBothFetched = isListFetchedKs && isListFetchedGuru

  // Items based on active tab
  const items = activeTabName !== 'GURU' ? itemsKs || [] : itemsGuru || []
  const itemsKsCount = itemsKs?.length || 0
  const itemsGuruCount = itemsGuru?.length || 0

  // Total items
  const totalItems = itemsKsCount + itemsGuruCount

  const metas = {
    ...metasKs,
    counts: summarizeObjects(metasKs?.counts, metasGuru?.counts),
  }

  useEffect(() => {
    if (isListBothFetched && !isListLoading) onBothFetched?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isListBothFetched])

  return {
    // Kepala Sekolah
    isListLoadingKs,
    isListErrorKs,
    isListSuccessKs,
    itemsKs,
    itemsCountKs: itemsKsCount,
    metasKs,
    refetchListKs,

    // Guru
    isListLoadingGuru,
    isListErrorGuru,
    isListSuccessGuru,
    itemsGuru,
    itemsCountGuru: itemsGuruCount,
    metasGuru,
    refetchListGuru,

    // Combined
    isListLoading,
    isListError,
    isListSuccess,
    isListBothFetched,
    metas,
    totalItems,
    hasItems: totalItems > 0,

    // Relative to active tab
    items,
    relativeItems: items, // Serve as an alias for items
    isRelativeLoading:
      activeTabName !== 'GURU' ? isListLoadingKs : isListLoadingGuru,
    isRelativeError: activeTabName !== 'GURU' ? isListErrorKs : isListErrorGuru,
    isRelativeSuccess:
      activeTabName !== 'GURU' ? isListSuccessKs : isListSuccessGuru,
  }
}

export default usePenetapanTahunanDinas
